import { useMemo, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import { Button } from '../ui/button';
import { AddLink } from '../ui/add-link';
import { ListRow, ROW_CLASS } from '../ui/row';
import { IconButton } from '../ui/icon-button';
import type { MenuEntry } from '../ui/row-menu';
import { ExternalLink, X } from 'lucide-react';
import { PaneSection } from './PaneParts.js';
import type { TranslationKeys } from '../../i18n/types.js';
import type { MaterialOp, MaterialRef } from './useBeatMaterials.js';
import { searchFilter } from '../../lib/search/engine';

type JournalType = 'npc' | 'location' | 'quest' | 'faction' | 'handout' | 'lore';
const JOURNAL_TYPES: readonly JournalType[] = ['npc', 'location', 'quest', 'faction', 'handout', 'lore'];

/** A campaign object as the materials UI shows it (resolved by the host from the stores). */
export interface MaterialItem {
  readonly ref: MaterialRef;
  readonly name: string;
  /** «NPC», «Лист NPC», … */
  readonly typeLabel: string;
  /** Tells same-named objects apart: a text snippet, the owner, a short id. */
  readonly context: string;
}

export interface BeatMaterialsProps {
  readonly beatLabel: string;
  /** The beat's confirmed materials; undefined item = the object no longer exists. */
  readonly materials: readonly { readonly ref: MaterialRef; readonly item: MaterialItem | undefined }[];
  /** Operations of THIS beat not confirmed yet. */
  readonly ops: readonly MaterialOp[];
  /** Every journal entry and sheet the GM can add. */
  readonly candidates: readonly MaterialItem[];
  /** The beat's prep source: shown in the search as such, not addable (one record, one role). */
  readonly sourceRef?: MaterialRef;
  readonly t: (key: keyof TranslationKeys) => string;
  readonly onAdd: (ref: MaterialRef) => void;
  readonly onRemove: (ref: MaterialRef) => void;
  readonly onOpen: (ref: MaterialRef) => void;
  /** Create a journal entry of this type and add it to the beat (name asked by the host). */
  readonly onCreate: (entryType: JournalType) => void;
  readonly onRetry: (key: string) => void;
  readonly onDismiss: (key: string) => void;
  /**
   * What a material shows under its row — a quest's objectives, ticked here
   * (GM Studio quests 1). Built by the host; nothing — a plain row.
   */
  readonly renderDetail?: (ref: MaterialRef) => ReactNode;
  /** Q-6: a quest card's header after the name — status pill and «from <giver>» (replaces the text snippet). */
  readonly renderHead?: (ref: MaterialRef) => ReactNode;
  /** A quest is shown in its own section «Beat quests» (mockup), as a card without the type badge. */
  readonly isQuest?: (ref: MaterialRef) => boolean;
}

/** GK-2: «Гоблин 1», «Гоблин 2» … of one kind (same type label) are one line «Гоблин ×4» (mockup). A name without a number stands alone. */
const NUMBERED = /^(.*\S) (\d+)$/;
type Material = { readonly ref: MaterialRef; readonly item: MaterialItem | undefined };
export type MaterialRow = { readonly kind: 'one'; readonly m: Material } | { readonly kind: 'group'; readonly base: string; readonly key: string; readonly members: readonly Material[] };
export function groupMaterials(list: readonly Material[]): readonly MaterialRow[] {
  const baseOf = (m: Material): string | undefined => {
    if (m.item === undefined || m.ref.kind !== 'sheet') return undefined;
    const hit = NUMBERED.exec(m.item.name);
    return hit === null ? undefined : `${m.item.typeLabel}|${hit[1]}`;
  };
  const counts = new Map<string, number>();
  for (const m of list) {
    const b = baseOf(m);
    if (b !== undefined) counts.set(b, (counts.get(b) ?? 0) + 1);
  }
  const rows: MaterialRow[] = [];
  const done = new Set<string>();
  for (const m of list) {
    const b = baseOf(m);
    if (b === undefined || (counts.get(b) ?? 0) < 2) {
      rows.push({ kind: 'one', m });
      continue;
    }
    if (done.has(b)) continue;
    done.add(b);
    const members = list.filter((x) => baseOf(x) === b);
    rows.push({ kind: 'group', base: b.split('|')[1] ?? '', key: members[0]?.ref.id ?? b, members });
  }
  return rows;
}

const same = (a: MaterialRef, b: MaterialRef): boolean => a.kind === b.kind && a.id === b.id;
const refTestId = (ref: MaterialRef): string => `${ref.kind}-${ref.id}`;

/**
 * «Materials» of a beat (GM Studio 3.1): the explicit list, added and removed
 * one object at a time. Opening a material opens it; opening a document
 * elsewhere never adds it here. What is shown as a material is what the server
 * confirmed — pending operations are separate rows until their answer.
 */
export function BeatMaterials({
  beatLabel,
  materials,
  ops,
  candidates,
  sourceRef,
  t,
  onAdd,
  onRemove,
  onOpen,
  onCreate,
  onRetry,
  onDismiss,
  renderDetail,
  renderHead,
  isQuest,
}: BeatMaterialsProps): JSX.Element {
  const [picking, setPicking] = useState(false);
  const [creating, setCreating] = useState(false);
  const [query, setQuery] = useState('');
  const [openGroups, setOpenGroups] = useState<ReadonlySet<string>>(new Set());

  const pendingAdds = ops.filter((o) => o.kind === 'add');
  const removing = (ref: MaterialRef): MaterialOp | undefined =>
    ops.find((o) => o.kind === 'remove' && same(o.ref, ref));
  const inBeat = (ref: MaterialRef): boolean => materials.some((m) => same(m.ref, ref));
  const nameOf = (ref: MaterialRef): string =>
    candidates.find((c) => same(c.ref, ref))?.name ?? materials.find((m) => same(m.ref, ref))?.item?.name ?? ref.id;

  // The app's one matcher (group M): typos, ё/е, the name first, then the type and where it lives; best first.
  const found = useMemo(
    (): readonly MaterialItem[] => searchFilter(query, candidates, (c) => [c.name, c.typeLabel, c.context]),
    [candidates, query],
  );

  const opStatus = (op: MaterialOp): string => {
    if (op.status === 'pending') {
      return op.kind === 'create'
        ? t('studio.materials.creating').replace('{name}', op.name)
        : t(op.kind === 'add' ? 'studio.materials.adding' : 'studio.materials.removing');
    }
    if (op.status === 'offline') return t('studio.materials.offline');
    if (op.status === 'unknown') return t('studio.materials.createUnknown');
    if (op.status === 'unresolved') return t('studio.materials.createUnresolved').replace('{name}', op.kind === 'create' ? op.name : '');
    const title =
      op.kind === 'create'
        ? t('studio.create.failed').replace('{name}', op.name)
        : t(op.kind === 'add' ? 'studio.materials.addFailed' : 'studio.materials.removeFailed');
    return op.error !== undefined && op.error !== '' ? `${title}: ${op.error}` : title;
  };

  /** The ⋯ of a material (П3/П5): every action in words; «Убрать из бита» is not a deletion — not red, no question. */
  const menuOf = (ref: MaterialRef, item: MaterialItem | undefined): MenuEntry[] => [
    { label: t('studio.materials.open'), icon: ExternalLink, hidden: item === undefined, onSelect: () => onOpen(ref) },
    { label: t('studio.materials.remove'), icon: X, testid: 'beat-material-remove', onSelect: () => onRemove(ref) },
  ];
  const openIcon = (ref: MaterialRef): JSX.Element => (
    <IconButton
      size="sm"
      label={t('studio.materials.open')}
      icon={<ExternalLink size={14} aria-hidden="true" />}
      data-testid="beat-material-open"
      onClick={(): void => onOpen(ref)}
    />
  );
  // P09b (Q-7): a sheet of the beat's materials dragged onto a map is a token of that sheet (as from the rail).
  const dragOf = (ref: MaterialRef, item: MaterialItem | undefined): Partial<React.HTMLAttributes<HTMLElement>> & { draggable?: boolean } =>
    item !== undefined && ref.kind === 'sheet'
      ? {
          draggable: true,
          onDragStart: (e: React.DragEvent): void => {
            e.dataTransfer.setData('text/plain', item.name);
            e.dataTransfer.setData('application/json', JSON.stringify({ dragType: 'character-sheet', sheetId: ref.id, sheetName: item.name }));
            e.dataTransfer.effectAllowed = 'copyMove';
          },
        }
      : {};

  const renderMaterial = ({ ref, item }: { readonly ref: MaterialRef; readonly item: MaterialItem | undefined }, depth = 0): JSX.Element => {
    const rm = removing(ref);
    const busy = rm !== undefined && rm.status !== 'failed';
    const detail = item !== undefined ? renderDetail?.(ref) : undefined;
    const head = item !== undefined ? renderHead?.(ref) : undefined;
    const questCard = item !== undefined && isQuest?.(ref) === true;
    if (questCard) {
      // A quest of the beat: a card (mockup quests__beat) — its head line is a row: the name opens it, the status and
      // the giver, then the open icon and ⋯; the objectives under it.
      return (
        <li key={refTestId(ref)} className="list-none rounded-md border border-line bg-panel-2" data-testid={`beat-material-${refTestId(ref)}`}>
          <ListRow
            as="div"
            className="px-2.5"
            label={<span className="text-body font-semibold">{item.name}</span>}
            tip={item.name}
            onOpen={(): void => onOpen(ref)}
            labelTestid="beat-material-name"
            pills={head ?? undefined}
            meta={busy ? opStatus(rm) : undefined}
            actions={busy ? undefined : openIcon(ref)}
            menu={busy ? undefined : menuOf(ref, item)}
            menuTestid="beat-material-menu"
            alwaysActions
          />
          {detail !== undefined && detail !== null ? <div className="border-t border-line px-2.5 pb-1.5 pt-1">{detail}</div> : null}
        </li>
      );
    }
    return (
      <ListRow
        key={refTestId(ref)}
        data-testid={`beat-material-${refTestId(ref)}`}
        {...dragOf(ref, item)}
        depth={depth}
        label={item !== undefined ? item.name : <span className="text-dim" data-testid="beat-material-unavailable">{t('studio.materials.unavailable')}</span>}
        tip={item !== undefined ? (item.context !== '' ? `${item.name} — ${item.context}` : item.name) : t('studio.materials.unavailable')}
        onOpen={item !== undefined ? (): void => onOpen(ref) : undefined}
        labelTestid="beat-material-name"
        meta={busy ? opStatus(rm) : item?.typeLabel}
        actions={busy || item === undefined ? undefined : openIcon(ref)}
        menu={busy ? undefined : menuOf(ref, item)}
        menuTestid="beat-material-menu"
        after={detail !== undefined && detail !== null ? <li className="list-none pb-1 pl-6">{detail}</li> : undefined}
      />
    );
  };

  return (
    <div data-testid="beat-materials">
      {materials.some((m) => m.item !== undefined && isQuest?.(m.ref) === true) ? (
        <PaneSection title={t('studio.materials.quests')} className="mb-3">
          <ul className="flex flex-col gap-2">
            {materials.filter((m) => m.item !== undefined && isQuest?.(m.ref) === true).map((m) => renderMaterial(m))}
          </ul>
        </PaneSection>
      ) : null}
      <PaneSection title={t('studio.materials.title')}>

      {materials.length === 0 && ops.length === 0 ? (
        <p className="mb-2 text-meta leading-relaxed text-dim" data-testid="beat-materials-empty">
          {t('studio.materials.empty')}
        </p>
      ) : null}

      <ul className="mb-1 flex flex-col">
        {groupMaterials(materials.filter((m) => isQuest?.(m.ref) !== true || m.item === undefined)).map((row) => {
          if (row.kind === 'one') return renderMaterial(row.m);
          const expanded = openGroups.has(row.key);
          const toggle = (): void =>
            setOpenGroups((prev) => {
              const next = new Set(prev);
              if (next.has(row.key)) next.delete(row.key);
              else next.add(row.key);
              return next;
            });
          // «Гоблин ×4»: a folder line of the tree (П1б/П9) — the twisty and the name both fold it.
          return (
            <li key={`group-${row.key}`} className="list-none" data-testid="beat-material-group">
              <ListRow
                as="div"
                label={<span data-testid="beat-material-group-name">{`${row.base} ×${row.members.length}`}</span>}
                tip={t(expanded ? 'studio.materials.groupFold' : 'studio.materials.groupUnfold')}
                onOpen={toggle}
                labelTestid="beat-material-group-toggle"
                aria-expanded={expanded}
                twisty={{ open: expanded, onToggle: toggle }}
                meta={row.members[0]?.item?.typeLabel}
              />
              {expanded ? <ul className="flex flex-col">{row.members.map((m) => renderMaterial(m, 1))}</ul> : null}
            </li>
          );
        })}
        {ops
          .filter((op) => op.kind !== 'remove' || op.status === 'failed')
          .filter((op) => !(op.kind === 'add' && inBeat(op.ref)))
          .map((op) => (
            <li
              key={op.key}
              className={`flex min-w-0 items-center gap-2 rounded-md border px-2.5 py-1.5 text-meta ${
                op.status === 'failed' ? 'border-danger/30 text-danger' : op.status === 'unresolved' ? 'border-hl/30 text-hl/90' : 'border-dashed border-line text-dim'
              }`}
              data-testid="beat-material-op"
              data-status={op.status}
            >
              {op.kind !== 'create' ? <span className="min-w-0 truncate text-fg">{nameOf(op.ref)}</span> : null}
              <span className="min-w-0 flex-1">{opStatus(op)}</span>
              {op.status === 'failed' && !(op.kind !== 'create' && op.final === true) ? (
                <Button type="button" size="sm" variant="danger" className="flex-none" data-testid="beat-material-retry" onClick={(): void => onRetry(op.key)}>
                  {t('studio.materials.retry')}
                </Button>
              ) : null}
              {op.status === 'failed' || op.status === 'unresolved' ? (
                <Button type="button" variant="ghost" size="inline" className="flex-none px-1 text-muted-foreground" data-testid="beat-material-dismiss" onClick={(): void => onDismiss(op.key)}>
                  {t('studio.materials.dismiss')}
                </Button>
              ) : null}
            </li>
          ))}
      </ul>

      {!picking ? (
        <AddLink plus={false} data-testid="beat-materials-add" onClick={(): void => setPicking(true)}>
          {t('studio.materials.add')}
        </AddLink>
      ) : (
        <div className="flex flex-col gap-2 rounded-md border border-line bg-panel-2 px-2.5 py-2" data-testid="beat-materials-picker">
          <div className="flex items-center gap-2">
            <span className="min-w-0 flex-1 truncate text-meta font-semibold text-fg" data-testid="beat-materials-picker-title">
              {t('studio.materials.addTo').replace('{beat}', beatLabel)}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="inline"
              className="flex-none px-1 text-muted-foreground"
              data-testid="beat-materials-close"
              onClick={(): void => {
                setPicking(false);
                setCreating(false);
                setQuery('');
              }}
            >
              {t('studio.materials.close')}
            </Button>
          </div>
          <input
            type="text"
            autoFocus
            className="w-full rounded border border-line bg-background px-2 py-1 text-body-s text-fg placeholder:text-dim focus:border-hl/50 focus:outline-none"
            placeholder={t('studio.materials.search')}
            data-testid="beat-materials-search"
            value={query}
            onChange={(e): void => setQuery(e.target.value)}
          />
          <ul className="flex max-h-56 flex-col gap-0.5 overflow-y-auto">
            {found.length === 0 ? (
              <li className="px-2 py-1 text-meta text-dim">{t('studio.materials.nothingFound')}</li>
            ) : null}
            {found.map((c) => {
              const added = inBeat(c.ref);
              const isSource = sourceRef !== undefined && same(sourceRef, c.ref);
              const adding = pendingAdds.some((o) => o.kind === 'add' && same(o.ref, c.ref) && o.status !== 'failed');
              return (
                <li key={refTestId(c.ref)}>
                  <button
                    type="button"
                    className={`${ROW_CLASS} disabled:cursor-default disabled:opacity-60 disabled:hover:bg-transparent`}
                    data-testid={`beat-materials-candidate-${refTestId(c.ref)}`}
                    disabled={added || adding || isSource}
                    onClick={(): void => onAdd(c.ref)}
                  >
                    <span className="flex-none rounded-full border border-line px-2 py-px text-pill text-muted-foreground">
                      {c.typeLabel}
                    </span>
                    <span className="min-w-0 truncate text-fg">{c.name}</span>
                    <span className="min-w-0 flex-1 truncate text-meta text-dim">{c.context}</span>
                    {isSource ? (
                      <span className="flex-none text-label text-hl/80" data-testid="beat-materials-candidate-source">
                        {t('studio.materials.isPrepSource')}
                      </span>
                    ) : added || adding ? (
                      <span className="flex-none text-label text-hl/80">
                        {added ? t('studio.materials.inBeat') : t('studio.materials.adding')}
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
          {!creating ? (
            <AddLink plus={false} data-testid="beat-materials-create" onClick={(): void => setCreating(true)}>
              {t('studio.materials.create')}
            </AddLink>
          ) : (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-meta text-dim">{t('studio.materials.chooseType')}</span>
              {JOURNAL_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  className="rounded-full border border-line px-2.5 py-0.5 text-meta text-muted-foreground hover:border-ai hover:text-fg"
                  data-testid={`beat-materials-create-${type}`}
                  onClick={(): void => {
                    setCreating(false);
                    onCreate(type);
                  }}
                >
                  {t(`journal.type.${type}` as keyof TranslationKeys)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      </PaneSection>
    </div>
  );
}

export default BeatMaterials;
